import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { FormViewer, createView, define, event, number, string } from '@react-form-builder/core'
import { signatureComponent } from '@react-form-builder/components-signature'
import {
  models,
  MuiViewerWrapper,
  MuiLocalizationProvider,
} from '@react-form-builder/components-material-ui'
import WOFormService from 'src/Services/API/woform.service'
import { drawStoredSignatures, generateFormPdf } from './formPdf'
import RSCSLogo from './customComponents/RSCSLogo'
import TechnicianDropdown from './customComponents/TechnicianDropdown'

const signatureModel = signatureComponent.build().model
// Type id 'RSCSLogo' kept for saved-form compatibility; renders the current
// company's logo (see RSCSLogo.jsx).
const rscsLogoModel = define(RSCSLogo, 'RSCSLogo')
  .name('Company Logo')
  .category('Custom')
  .props({
    width: number.default(140),
    height: string.default('auto'),
    alt: string.default('Company Logo'),
  })
  .build()
  .model

const technicianDropdownModel = define(TechnicianDropdown, 'TechnicianDropdown')
  .name('Technician Dropdown')
  .category('Custom')
  .props({
    value: string.valued,
    woId: number.default(0),
    label: string.default('Technician'),
    placeholder: string.default('Select technician'),
    onChange: event,
  })
  .build()
  .model

const customView = createView([...models, signatureModel, rscsLogoModel, technicianDropdownModel])
  .withViewerWrapper(MuiViewerWrapper)
  .withViewerWrapper(MuiLocalizationProvider)

const PARTIALLY_FILLED_STATUS = 2

const resolveNextStatus = (currentStatus, nextStatus, nextData, nextIsSubmitted, currentData) => {
  if (nextStatus !== undefined && nextStatus !== null) return nextStatus
  if (nextIsSubmitted) return currentStatus

  const currentSerialized = JSON.stringify(currentData || {})
  const nextSerialized = JSON.stringify(nextData || {})

  if (currentSerialized !== nextSerialized && currentStatus < PARTIALLY_FILLED_STATUS) {
    return PARTIALLY_FILLED_STATUS
  }

  return currentStatus
}


const FormViewerComponent = () => {
  const { formId } = useParams()
  const [formData, setFormData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const formContentRef = useRef(null)
  const userInteractedRef = useRef(false)
  const lastPostedDataRef = useRef('{}')

  useEffect(() => {
    if (!formId) return
    setLoading(true)
    setError(null)
    WOFormService.findOne(formId)
      .then((res) => {
        setFormData(res.data)
      })
      .catch((err) => {
        setError(JSON.stringify(err))
      })
      .finally(() => setLoading(false))
  }, [formId])

  const getForm = useCallback(() => {
    if (!formData) return '{}'
    const schema = formData.schema || formData
    return typeof schema === 'string' ? schema : JSON.stringify(schema)
  }, [formData])

  const initialData = useMemo(() => {
    if (!formData?.data) return undefined
    return typeof formData.data === 'string'
      ? JSON.parse(formData.data)
      : formData.data
  }, [formData])

  const serializeFormData = useCallback((data) => {
    return JSON.stringify(data || {})
  }, [])

  useEffect(() => {
    userInteractedRef.current = false
    lastPostedDataRef.current = serializeFormData(initialData)
  }, [formId, initialData, serializeFormData])

  useEffect(() => {
    const container = formContentRef.current
    if (!container) return undefined

    const markInteracted = () => {
      userInteractedRef.current = true
    }

    container.addEventListener('input', markInteracted, true)
    container.addEventListener('change', markInteracted, true)
    container.addEventListener('pointerdown', markInteracted, true)
    container.addEventListener('keydown', markInteracted, true)

    return () => {
      container.removeEventListener('input', markInteracted, true)
      container.removeEventListener('change', markInteracted, true)
      container.removeEventListener('pointerdown', markInteracted, true)
      container.removeEventListener('keydown', markInteracted, true)
    }
  }, [formData])

  const setRef = useCallback((viewer) => {
    if (viewer) {
      window.__formViewer = viewer
    }
  }, [])

  const handleFormDataChange = useCallback((nextFormData) => {
    if (!window.ReactNativeWebView) return

    const payload = nextFormData?.data || {}
    const serialized = serializeFormData(payload)

    // Ignore changes that fire before the user actually touches the form.
    // The form library applies schema defaults during initial render, which
    // would otherwise trigger an autosave and flip the status from
    // "assigned" to "partially filled" without any user input.
    if (!userInteractedRef.current) {
      lastPostedDataRef.current = serialized
      return
    }

    if (serialized === lastPostedDataRef.current) return

    lastPostedDataRef.current = serialized
    window.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'FORM_CHANGE', payload })
    )
  }, [serializeFormData])

  useEffect(() => {
    const handleExternalFormUpdate = (event) => {
      const detail = event.detail || {}
      const nextData = detail.data || {}
      const nextSerialized = serializeFormData(nextData)

      setFormData((current) => {
        if (!current) return current

        const currentSerialized = serializeFormData(current.data)
        const nextIsSubmitted =
          typeof detail.isSubmitted === 'boolean'
            ? detail.isSubmitted
            : current.isSubmitted
        const nextStatus = resolveNextStatus(
          current.status,
          detail.status,
          nextData,
          nextIsSubmitted,
          current.data
        )

        if (
          currentSerialized === nextSerialized &&
          current.status === nextStatus &&
          current.isSubmitted === nextIsSubmitted
        ) {
          return current
        }

        userInteractedRef.current = false
        lastPostedDataRef.current = nextSerialized

        return {
          ...current,
          data: nextData,
          status: nextStatus,
          isSubmitted: nextIsSubmitted,
        }
      })
    }

    window.addEventListener('makani-form-external-update', handleExternalFormUpdate)

    return () => {
      window.removeEventListener('makani-form-external-update', handleExternalFormUpdate)
    }
  }, [serializeFormData])

  const isSubmitted = !!formData?.isSubmitted

  useEffect(() => {
    const woId = formData?.wo?.id ? Number(formData.wo.id) : null
    window.__makaniFormWoId = woId
    return () => {
      delete window.__makaniFormWoId
    }
  }, [formData?.wo?.id])

  // The form viewer applies stored data after mount, but stored signatures are
  // only drawn into their canvases on mount — retry on timers until the data
  // has landed (drawStoredSignatures is a no-op on non-blank canvases).
  useEffect(() => {
    if (!initialData) return undefined
    const timers = [300, 800, 1500].map((delay) =>
      setTimeout(() => {
        drawStoredSignatures(formContentRef.current, initialData, getForm())
      }, delay)
    )
    return () => timers.forEach(clearTimeout)
  }, [initialData, getForm])

  useEffect(() => {
    window.__downloadFormPdf = async () => {
      if (!formContentRef.current) return
      try {
        const element = formContentRef.current
        await drawStoredSignatures(element, initialData, getForm())
        const pdf = await generateFormPdf(element)

        if (window.ReactNativeWebView) {
          const base64 = pdf.output('datauristring')
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'PDF_DOWNLOAD', data: base64, fileName: formData?.name || 'form' })
          )
        } else {
          pdf.save(`${formData?.name || 'form'}.pdf`)
        }
      } catch (err) {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(
            JSON.stringify({ type: 'PDF_ERROR', message: err.message })
          )
        }
        console.error('PDF download failed:', err)
      }
    }
    return () => { delete window.__downloadFormPdf }
  }, [formData, initialData, getForm])

  if (loading) {
    return <div style={{ maxWidth: 600, margin: '0 auto', padding: 10 }}></div>
  }

  if (error) {
    return <div style={{ maxWidth: 600, margin: '0 auto', padding: 10, color: 'red' }}>{error}</div>
  }

  if (!formData) {
    return <div style={{ maxWidth: 600, margin: '0 auto', padding: 10 }}>No form data</div>
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: 10, minHeight: '100dvh' }}>
      <div className="relative rounded-md overflow-hidden min-h-[calc(100dvh-20px)]">
        <div ref={formContentRef} style={{ pointerEvents: isSubmitted ? 'none' : 'auto' }}>
          <FormViewer
            key={formId}
            view={customView}
            getForm={getForm}
            initialData={initialData}
            viewerRef={setRef}
            onFormDataChange={handleFormDataChange}
            readOnly={isSubmitted}
          />
        </div>
        {isSubmitted && (
          <div
            className="absolute inset-0 bg-[#8BD38E] bg-opacity-20 rounded-md pointer-events-none"
            style={{ zIndex: 10 }}
          />
        )}
      </div>
    </div>
  )
}

export default FormViewerComponent
